import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { ChangeEvent } from 'react';
import {
  VenetianMask, Palette, Type, Smile, Share2, AtSign, Users,
  Sparkles, Upload, ShieldCheck, MessageSquare, Clock, Search, Send,
} from 'lucide-react';
import { The, AnhDaiDien, DangTai, ChuaCoDuLieu } from '../components/ui';
import { BackgroundPickerDialog } from '../components/BackgroundPickerDialog';
import { api, LoiMayChu, resolveSubmitAccessError } from '../services/api';
import type { LocalMediaRecord, MediaMimeType, Nhom, TrangThaiAI, BackgroundPresetItem } from '../services/kieu';
import { choPhepAnDanh, chiaNhomDangCheo } from '../services/taiKhoan';
import { useKhoaGui } from '../services/khoaGui';
import { mediaVault } from '../services/mediaVault';
import {
  CAPABILITY_KEYS,
  isCapabilityEnabled,
  getCapabilityReason,
  formatPlannedActionNotice,
  getMediaCapabilityStatus,
  sanitizeDraftWithCapabilities,
  validateSubmitWithCapabilities,
  type ProductCapabilitiesMap,
} from '../services/productCapabilities';
import {
  VERIFIED_BACKGROUND_PRESET_RED,
  VERIFIED_PRESET_NAME,
  getBackgroundIncompatibilityReason,
  validateBackgroundOnSubmit,
  sanitizeDraftBackground,
  validateAndSanitizePresetList,
  getPresetSwatchStyle,
  applyBackgroundToPayload,
} from '../services/backgroundPost';

/**
 * Màn soạn và gửi bài đăng.
 *
 * Bố cục, nhãn và lớp Tailwind lấy từ bản đang chạy (đọc DOM qua CDP).
 * Quy tắc nghiệp vụ lấy từ `lab-server/schemas/dispatch.schema.js` — nguồn
 * sự thật duy nhất, vì máy chủ mới là nơi quyết định chấp nhận hay từ chối.
 */

// ===== Hằng số lấy từ dispatch.schema.js =====
const TOI_DA_KY_TU = 63206;
const TOI_DA_NHOM = 100;
const GIAN_CACH_TOI_THIEU = 10;
const GIAN_CACH_TOI_DA = 3600;
/** Nêu bật quét spam tag rất gắt -> sàn giãn cách ×3 (schema dòng 51-54). */
const GIAN_CACH_TOI_THIEU_NEU_BAT = 30;
const TOI_DA_DANG_CHEO = 9;
const TOI_DA_BINH_LUAN = 2000;
// Phông nền - Sắp có: các preset màu sắc khác; hiện hỗ trợ phông nền cho bài viết nhóm (giới hạn 130 ký tự / 4 dòng đã gỡ bỏ per user request).
const MEDIA_MIME = new Set<MediaMimeType>(['image/jpeg', 'image/png', 'image/webp', 'video/mp4']);
const MAX_IMAGE_BYTES = 10 * 1024 * 1024 - 1;
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
const MAX_TOTAL_IMAGE_BYTES = 50 * 1024 * 1024;
const MAX_IMAGES = 10;

const NUT_CONG_CU = 'inline-flex h-8 w-8 items-center justify-center rounded-lg transition';
const NUT_THUONG = 'text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700';
const NUT_BAT = 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400';
const NUT_KHOA = 'cursor-not-allowed text-slate-300 dark:text-slate-600';
const O_SO = 'w-16 rounded-md border border-slate-300 px-2 py-1.5 text-center text-sm font-semibold text-slate-800 outline-none focus:border-brand-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100';
const KHOI_TUY_CHON = 'mt-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700';
const CREATE_POST_DRAFT_KEY = 'fbmax_draft_taobai';

type CreatePostDraft = {
  content: string;
  selectedGroupIds: string[];
  anonymous: boolean;
  highlight: boolean;
  draftCrossPostEnabled: boolean;
  postToProfile: boolean;
  delayFromSec: number;
  delayToSec: number;
  antiSpam: boolean;
  enableFirstComment: boolean;
  firstComment: string;
  scheduledAt: string;
  repeatDaily: boolean;
  media: LocalMediaRecord[];
  backgroundId?: string | null;
  updatedAt: number;
};

type MediaUiItem = { record: LocalMediaRecord; previewUrl?: string };

function buildDraftStorageKey(tenantId?: string, fbUid?: string) {
  return `${CREATE_POST_DRAFT_KEY}:${tenantId || 'no-tenant'}:${fbUid || 'no-actor'}`;
}

function readDraft(tenantId?: string, fbUid?: string): CreatePostDraft | null {
  try {
    const value = JSON.parse(localStorage.getItem(buildDraftStorageKey(tenantId, fbUid)) || 'null');
    return value && typeof value === 'object' && typeof value.content === 'string' ? value as CreatePostDraft : null;
  } catch { return null; }
}

function CongTac({ bat }: { bat: boolean }) {
  return (
    <span className={'relative h-5 w-9 rounded-full transition-colors ' + (bat ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600')}>
      <span className={'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ' + (bat ? 'left-[1.125rem]' : 'left-0.5')} />
    </span>
  );
}

export function ManTaoBai({
  onXong,
  tenantId,
  fbUid,
  dangDungPage = false,
  onYeuCauNangCap,
}: {
  onXong?: () => void;
  tenantId?: string;
  fbUid?: string;
  dangDungPage?: boolean;
  onYeuCauNangCap?: () => void;
}) {
  const [noiDung, setNoiDung] = useState('');
  const [nhom, setNhom] = useState<Nhom[]>([]);
  const [daChon, setDaChon] = useState<Set<string>>(new Set());
  const [timNhom, setTimNhom] = useState('');
  const [dangTaiNhom, setDangTaiNhom] = useState(true);
  const [lyDoTrongNhom, setLyDoTrongNhom] = useState<string | null>(null);

  const [anDanh, setAnDanh] = useState(false);
  const [neuBat, setNeuBat] = useState(false);
  const [dangCheo, setDangCheo] = useState(false);
  const [dangLenTrang, setDangLenTrang] = useState(false);

  const [tuGiay, setTuGiay] = useState(60);
  const [denGiay, setDenGiay] = useState(120);
  const [neSpam, setNeSpam] = useState(true);

  const [batBinhLuan, setBatBinhLuan] = useState(false);
  const [binhLuan, setBinhLuan] = useState('');

  const [henGio, setHenGio] = useState('');
  const [lapHangNgay, setLapHangNgay] = useState(false);

  const [ai, setAi] = useState<TrangThaiAI | null>(null);
  const [dangGui, setDangGui] = useState(false);
  const [thongBao, setThongBao] = useState<{ loai: 'loi' | 'xong'; chu: string } | null>(null);
  const [vaultReady, setVaultReady] = useState(false);
  const [media, setMedia] = useState<MediaUiItem[]>([]);
  const [isImportingMedia, setIsImportingMedia] = useState(false);
  const [mediaProgress, setMediaProgress] = useState(0);
  const [backgroundId, setBackgroundId] = useState<string | null>(null);
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [presetsCatalog, setPresetsCatalog] = useState<BackgroundPresetItem[] | null>(null);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [presetsError, setPresetsError] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<ProductCapabilitiesMap | null>(null);
  const [isCapabilitiesLoaded, setIsCapabilitiesLoaded] = useState(false);
  const [capabilitiesError, setCapabilitiesError] = useState<string | null>(null);
  const [isUnpaid, setIsUnpaid] = useState(false);
  const activeTenantAndActorRef = useRef({ tenantId, fbUid });
  const latestPresetsRequestIdRef = useRef(0);
  const latestCapabilitiesRequestIdRef = useRef(0);
  const colorPaletteRef = useRef<HTMLDivElement>(null);
  const oNhap = useRef<HTMLTextAreaElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef(noiDung);
  const mediaRef = useRef<MediaUiItem[]>([]);
  const restoredPreviewKeyRef = useRef('');
  // AI Spin là thao tác GHI (tốn lượt hạn mức của cả workspace, tính tiền thật
  // vào hoá đơn API của chủ dự án) nên phải khoá bằng ref theo UI-01, không chỉ
  // bằng state. Xem services/khoaGui.ts.
  const [dangSpin, chaySpin] = useKhoaGui();

  useEffect(() => {
    activeTenantAndActorRef.current = { tenantId, fbUid };
  }, [tenantId, fbUid]);

  const fetchPresets = useCallback(async (_isManualRefresh = false) => {
    if (!tenantId) return;
    const requestTenant = tenantId;
    const requestActor = fbUid;
    const requestId = ++latestPresetsRequestIdRef.current;
    setIsLoadingPresets(true);
    setPresetsError(null);
    try {
      const res = await api.backgroundPresets();
      // Guard against stale response if a newer fetch was initiated or actor/tenant switched (IDENTITY-01, SEC-01)
      if (
        requestId !== latestPresetsRequestIdRef.current ||
        activeTenantAndActorRef.current.tenantId !== requestTenant ||
        activeTenantAndActorRef.current.fbUid !== requestActor
      ) {
        return;
      }
      if (!res || !Array.isArray(res.presets)) {
        throw new Error('Dữ liệu danh mục phông nền không hợp lệ.');
      }
      const sanitized = validateAndSanitizePresetList(res.presets);
      setPresetsCatalog(sanitized);

      // Check current backgroundId against freshly loaded catalog
      // Do not silently drop preset; clearly notify and block intent if no longer allowed
      setBackgroundId((currentBg) => {
        if (!currentBg) return null;
        const matched = sanitized.find((p) => p.id === currentBg);
        if (!matched || !matched.enabled) {
          setThongBao({
            loai: 'loi',
            chu: `Phông nền đã chọn (${currentBg}) không còn khả dụng trên máy chủ. Vui lòng chọn phông nền khác hoặc bấm Không nền.`,
          });
        }
        return currentBg;
      });
    } catch (err) {
      if (
        requestId !== latestPresetsRequestIdRef.current ||
        activeTenantAndActorRef.current.tenantId !== requestTenant ||
        activeTenantAndActorRef.current.fbUid !== requestActor
      ) {
        return;
      }
      const msg =
        err instanceof LoiMayChu
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Không tải được danh mục phông nền từ máy chủ.';
      setPresetsError(msg);
    } finally {
      if (
        requestId === latestPresetsRequestIdRef.current &&
        activeTenantAndActorRef.current.tenantId === requestTenant &&
        activeTenantAndActorRef.current.fbUid === requestActor
      ) {
        setIsLoadingPresets(false);
      }
    }
  }, [tenantId, fbUid]);

  const fetchCapabilities = useCallback(async () => {
    if (!tenantId) return;
    const requestTenant = tenantId;
    const requestActor = fbUid;
    const requestId = ++latestCapabilitiesRequestIdRef.current;
    setCapabilitiesError(null);

    try {
      const res = await api.productCapabilities();
      if (
        requestId !== latestCapabilitiesRequestIdRef.current ||
        activeTenantAndActorRef.current.tenantId !== requestTenant ||
        activeTenantAndActorRef.current.fbUid !== requestActor
      ) {
        return;
      }
      if (res && res.capabilities) {
        setCapabilities(res.capabilities);
        setIsCapabilitiesLoaded(true);
        setIsUnpaid(false);
      }
    } catch (err) {
      if (
        requestId !== latestCapabilitiesRequestIdRef.current ||
        activeTenantAndActorRef.current.tenantId !== requestTenant ||
        activeTenantAndActorRef.current.fbUid !== requestActor
      ) {
        return;
      }
      if (err instanceof LoiMayChu && err.trangThai === 402) {
        setIsUnpaid(true);
        setIsCapabilitiesLoaded(true); // Preserve static preview per UI-04 on 402
        return;
      }
      const msg =
        err instanceof LoiMayChu
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Không tải được danh mục tính năng từ máy chủ.';
      setCapabilitiesError(msg);
      setIsCapabilitiesLoaded(false);
    }
  }, [tenantId, fbUid]);

  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  useEffect(() => { contentRef.current = noiDung; }, [noiDung]);
  useEffect(() => { mediaRef.current = media; }, [media]);

  useEffect(() => {
    const check = () => setVaultReady(document.documentElement.getAttribute('data-fbmax-media-vault-ready') === '1');
    check();
    window.addEventListener('fbmax-media-vault-ready', check);
    return () => window.removeEventListener('fbmax-media-vault-ready', check);
  }, []);

  useEffect(() => () => {
    for (const item of mediaRef.current) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
  }, []);

  useEffect(() => {
    const draft = readDraft(tenantId, fbUid);
    if (!draft) {
      setNoiDung('');
      contentRef.current = '';
      setDaChon(new Set());
      setAnDanh(false);
      setNeuBat(false);
      setDangCheo(false);
      setDangLenTrang(false);
      setTuGiay(60);
      setDenGiay(120);
      setNeSpam(true);
      setBatBinhLuan(false);
      setBinhLuan('');
      setHenGio('');
      setLapHangNgay(false);
      for (const item of mediaRef.current) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      setMedia([]);
      setBackgroundId(null);
      setShowColorPalette(false);
      setPresetsCatalog(null);
      setPresetsError(null);
      setIsLoadingPresets(false);
      return;
    }
    const { sanitized: sanitizedDraft, notices } = sanitizeDraftWithCapabilities(draft, capabilities);
    if (notices.length > 0) {
      setThongBao({ loai: 'loi', chu: notices.join(' ') });
    } else {
      setThongBao({ loai: 'xong', chu: 'Đã khôi phục bản nháp lưu trên trình duyệt này.' });
    }
    setNoiDung(sanitizedDraft.content);
    contentRef.current = sanitizedDraft.content;
    setDaChon(new Set(Array.isArray(sanitizedDraft.selectedGroupIds) ? sanitizedDraft.selectedGroupIds.map(String) : []));
    setAnDanh(dangDungPage ? false : sanitizedDraft.anonymous === true);
    setNeuBat(sanitizedDraft.highlight === true);
    setDangCheo(sanitizedDraft.draftCrossPostEnabled === true);
    setDangLenTrang(sanitizedDraft.postToProfile === true);
    setTuGiay(Number.isFinite(sanitizedDraft.delayFromSec) ? sanitizedDraft.delayFromSec : 60);
    setDenGiay(Number.isFinite(sanitizedDraft.delayToSec) ? sanitizedDraft.delayToSec : 120);
    setNeSpam(sanitizedDraft.antiSpam !== false);
    setBatBinhLuan(sanitizedDraft.enableFirstComment === true);
    setBinhLuan(typeof sanitizedDraft.firstComment === 'string' ? sanitizedDraft.firstComment : '');
    setHenGio(typeof sanitizedDraft.scheduledAt === 'string' ? sanitizedDraft.scheduledAt : '');
    setLapHangNgay(sanitizedDraft.repeatDaily === true);
    setMedia(Array.isArray(sanitizedDraft.media) ? sanitizedDraft.media.map(record => ({ record })) : []);
    const validBg = sanitizeDraftBackground(sanitizedDraft.backgroundId, {
      isPage: dangDungPage,
      postToProfile: sanitizedDraft.postToProfile === true,
      anDanh: sanitizedDraft.anonymous === true,
      isCrossPost: sanitizedDraft.draftCrossPostEnabled === true,
      hasMedia: Array.isArray(sanitizedDraft.media) && sanitizedDraft.media.length > 0,
      charCount: sanitizedDraft.content.length,
      lineCount: sanitizedDraft.content.split('\n').length,
    }, null);
    setBackgroundId(validBg);
    if (validBg) {
      fetchPresets();
    }
  }, [tenantId, fbUid, dangDungPage, fetchPresets, capabilities]);

  useEffect(() => {
    if (!vaultReady || !tenantId || media.length === 0) return;
    const ids = media.map(item => item.record.localMediaId).sort();
    const key = `${tenantId}:${ids.join(',')}`;
    if (restoredPreviewKeyRef.current === key) return;
    restoredPreviewKeyRef.current = key;
    let cancelled = false;
    (async () => {
      try {
        const statuses = await mediaVault.status(tenantId, ids);
        const available = statuses.filter((item): item is LocalMediaRecord => item.status === 'AVAILABLE');
        const restored: MediaUiItem[] = [];
        for (const record of available) {
          let previewUrl;
          if (record.mimeType.startsWith('image/')) {
            const preview = await mediaVault.preview(tenantId, record.localMediaId);
            previewUrl = URL.createObjectURL(preview.blob);
          }
          restored.push({ record, previewUrl });
        }
        if (!cancelled) {
          for (const item of mediaRef.current) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
          setMedia(restored);
          if (restored.length !== media.length) {
            setThongBao({ loai: 'loi', chu: 'Một số tệp trong bản nháp không còn tồn tại trên thiết bị này.' });
          }
        }
      } catch (error) {
        if (!cancelled) setThongBao({ loai: 'loi', chu: error instanceof Error ? error.message : 'Không đọc được media trong bản nháp.' });
      }
    })();
    return () => { cancelled = true; };
  }, [vaultReady, tenantId, media]);

  useEffect(() => {
    api.nhom(fbUid)
      .then(r => { setNhom(r.groups || []); setLyDoTrongNhom(r.reason ? (r.message || r.reason) : null); })
      .catch(e => setLyDoTrongNhom(e instanceof LoiMayChu ? e.message : 'Không tải được danh sách nhóm.'))
      .finally(() => setDangTaiNhom(false));
    api.trangThaiAI().then(setAi).catch(() => {});
  }, [fbUid]);

  useEffect(() => { if (dangDungPage) setAnDanh(false); }, [dangDungPage]);
  useEffect(() => {
    if (dangDungPage && backgroundId) {
      setBackgroundId(null);
      setThongBao({ loai: 'loi', chu: 'Đã tắt phông nền: Phông nền hiện chỉ hỗ trợ tài khoản cá nhân, không hỗ trợ Fanpage.' });
    }
  }, [dangDungPage, backgroundId]);

  useEffect(() => {
    if (media.length > 0 && backgroundId) {
      setBackgroundId(null);
      setThongBao({ loai: 'loi', chu: 'Đã tắt phông nền: Không thể dùng phông nền cùng ảnh hoặc video.' });
    }
  }, [media.length, backgroundId]);

  useEffect(() => {
    if (dangLenTrang && backgroundId) {
      setBackgroundId(null);
      setThongBao({ loai: 'loi', chu: 'Đã tắt phông nền: Phông nền chỉ hỗ trợ đăng vào nhóm, chưa hỗ trợ đăng lên Trang cá nhân.' });
    }
  }, [dangLenTrang, backgroundId]);

  useEffect(() => {
    if (anDanh && backgroundId) {
      setBackgroundId(null);
      setThongBao({ loai: 'loi', chu: 'Đã tắt phông nền: Không thể dùng phông nền cùng Đăng ẩn danh.' });
    }
  }, [anDanh, backgroundId]);

  useEffect(() => {
    if (dangCheo && backgroundId) {
      setBackgroundId(null);
      setThongBao({ loai: 'loi', chu: 'Đã tắt phông nền: Không thể dùng phông nền cùng Đăng chéo.' });
    }
  }, [dangCheo, backgroundId]);

  useEffect(() => {
    if (!showColorPalette) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowColorPalette(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showColorPalette]);

  // Nêu bật ép sàn giãn cách lên 30s. Máy chủ cũng chặn, nhưng sửa sẵn ở đây
  // để khách không bị từ chối sau khi đã soạn xong bài.
  useEffect(() => {
    if (neuBat && tuGiay < GIAN_CACH_TOI_THIEU_NEU_BAT) {
      setTuGiay(GIAN_CACH_TOI_THIEU_NEU_BAT);
      setDenGiay(d => Math.max(d, GIAN_CACH_TOI_THIEU_NEU_BAT));
    }
  }, [neuBat, tuGiay]);

  const nhomLoc = useMemo(() => {
    const k = timNhom.trim().toLowerCase();
    return k ? nhom.filter(g => g.name.toLowerCase().includes(k)) : nhom;
  }, [nhom, timNhom]);

  const soMucTieu = daChon.size + (dangLenTrang ? 1 : 0);
  const hasAttachedMedia = media.length > 0;

  const mediaStatus = useMemo(() => getMediaCapabilityStatus(capabilities), [capabilities]);
  const isAnonEnabled = useMemo(() => isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_ANON_TEXT), [capabilities]);
  const isCrossPostEnabled = useMemo(() => isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_CROSSPOST), [capabilities]);
  const isProfileEnabled = useMemo(() => isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_PROFILE_TEXT), [capabilities]);
  const isPagePostingEnabled = useMemo(() => isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_PAGE_TEXT), [capabilities]);

  /**
   * Kiểm tra TRƯỚC khi gửi, dùng đúng quy tắc của schema máy chủ.
   * Thà báo lỗi ngay tại chỗ còn hơn để khách bấm gửi rồi nhận 400.
   */
  const loiHopLe = useMemo((): string | null => {
    // 1. Authoritative capability and billing check (fail-closed submit if capabilities unavailable)
    const capCheck = validateSubmitWithCapabilities({
      capabilities,
      isLoaded: isCapabilitiesLoaded,
      loadError: capabilitiesError,
      isUnpaid,
      isPage: dangDungPage,
      postToProfile: dangLenTrang,
      anonymousIntent: anDanh,
      isCrossPost: dangCheo,
      backgroundId,
      mediaMimeTypes: media.map(m => m.record.mimeType),
      enableFirstComment: batBinhLuan,
      hasSchedule: Boolean(henGio || lapHangNgay),
    });
    if (!capCheck.ok) return capCheck.reason;

    if (!noiDung.trim()) return 'Hãy nhập nội dung bài viết.';
    if (noiDung.length > TOI_DA_KY_TU) return 'Nội dung bài viết quá dài.';
    if (soMucTieu === 0) return 'Vui lòng chọn ít nhất một nhóm hoặc chọn đăng lên Trang cá nhân.';
    if (daChon.size > TOI_DA_NHOM) return `Mỗi chiến dịch chỉ được chọn tối đa ${TOI_DA_NHOM} nhóm.`;
    if (!Number.isFinite(tuGiay) || !Number.isFinite(denGiay)) return 'Khoảng nghỉ không hợp lệ.';
    if (tuGiay < GIAN_CACH_TOI_THIEU) return `Khoảng nghỉ tối thiểu là ${GIAN_CACH_TOI_THIEU} giây.`;
    if (denGiay < tuGiay) return 'Khoảng nghỉ kết thúc phải lớn hơn hoặc bằng khoảng bắt đầu.';
    if (denGiay > GIAN_CACH_TOI_DA) return 'Khoảng nghỉ tối đa là 3.600 giây.';
    if (neuBat && tuGiay < GIAN_CACH_TOI_THIEU_NEU_BAT) {
      return 'Khi bật Nêu bật, khoảng nghỉ tối thiểu là 30 giây để tránh bị Facebook khoá tài khoản.';
    }
    if (dangCheo && anDanh) {
      return 'Không thể dùng Đăng chéo cùng Đăng ẩn danh: mỗi nhóm có một hồ sơ ảo riêng nên Facebook sẽ từ chối cả gói.';
    }
    if (dangCheo && hasAttachedMedia) {
      return 'Đăng chéo với ảnh/video chưa được hỗ trợ. Hãy gỡ media hoặc tắt Đăng chéo.';
    }
    if (dangCheo && daChon.size > TOI_DA_DANG_CHEO + 1) {
      return `Đăng chéo tối đa ${TOI_DA_DANG_CHEO} nhóm (Facebook giới hạn 10 nhóm mỗi bài, tính cả nhóm đăng chính).`;
    }
    // Đăng chéo cần ÍT NHẤT hai nhóm: một nhóm gốc và một nhóm được nhân bản sang.
    // Chọn một nhóm (hoặc chỉ đăng Trang cá nhân) thì danh sách nhóm chéo rỗng và
    // máy chủ từ chối cả gói (dispatch.schema.js:63). Báo tại đây để khách không
    // soạn xong bài rồi mới nhận lỗi.
    if (dangCheo && daChon.size < 2) {
      return 'Đăng chéo cần ít nhất 2 nhóm: một nhóm đăng chính và ít nhất một nhóm được đăng chéo sang. Hãy chọn thêm nhóm hoặc tắt Đăng chéo.';
    }
    if (batBinhLuan && binhLuan.length > TOI_DA_BINH_LUAN) return 'Bình luận tối đa 2.000 ký tự.';
    if (henGio) {
      const at = new Date(henGio).getTime();
      if (!Number.isFinite(at)) return 'Thời điểm hẹn đăng không hợp lệ.';
      if (at < Date.now() - 60000) return 'Thời điểm hẹn đăng phải ở tương lai.';
    }
    if (backgroundId) {
      if (isLoadingPresets) {
        return 'Đang tải danh mục phông nền từ máy chủ, vui lòng đợi trong giây lát.';
      }
      if (presetsError && (!presetsCatalog || presetsCatalog.length === 0)) {
        return `Không thể xác thực phông nền với máy chủ: ${presetsError}. Hãy thử tải lại danh mục phông nền.`;
      }
      const errBg = validateBackgroundOnSubmit({
        backgroundId,
        presets: presetsCatalog,
        groupIdCount: daChon.size,
        isPage: dangDungPage,
        postToProfile: dangLenTrang,
        anDanh,
        isCrossPost: dangCheo,
        hasMedia: hasAttachedMedia,
        text: noiDung,
        charCount: noiDung.length,
        lineCount: noiDung.split('\n').length,
      });
      if (errBg) return errBg;
    }
    return null;
  }, [
    capabilities, isCapabilitiesLoaded, capabilitiesError, isUnpaid,
    noiDung, soMucTieu, daChon.size, tuGiay, denGiay, neuBat, dangCheo, anDanh,
    hasAttachedMedia, batBinhLuan, binhLuan, henGio, lapHangNgay, backgroundId,
    dangDungPage, dangLenTrang, isLoadingPresets, presetsError, presetsCatalog, media,
  ]);

  const backgroundIncompatibilityReason = useMemo(() => {
    return getBackgroundIncompatibilityReason({
      isPage: dangDungPage,
      postToProfile: dangLenTrang,
      anDanh,
      isCrossPost: dangCheo,
      hasMedia: hasAttachedMedia,
      text: noiDung,
      charCount: noiDung.length,
      lineCount: noiDung.split('\n').length,
    });
  }, [dangDungPage, dangLenTrang, anDanh, dangCheo, hasAttachedMedia, noiDung]);

  const selectedPreset = useMemo(() => {
    if (!backgroundId) return null;
    if (presetsCatalog !== null) {
      const found = presetsCatalog.find((p) => p.id === backgroundId);
      if (!found || !found.enabled) return null;
      return found;
    }
    // Restrict visual fallback to pending catalog only (presetsCatalog === null)
    if (backgroundId === VERIFIED_BACKGROUND_PRESET_RED) {
      return {
        id: VERIFIED_BACKGROUND_PRESET_RED,
        label: VERIFIED_PRESET_NAME,
        preview: { type: 'solid' as const, colors: ['#E41E3F'] },
        status: 'VERIFIED' as const,
        enabled: true,
        scope: 'GROUP_PERSONAL' as const,
      };
    }
    return null;
  }, [backgroundId, presetsCatalog]);

  const composerCardStyle = useMemo(() => {
    if (!backgroundId) return undefined;
    if (!selectedPreset?.preview) return undefined;
    return getPresetSwatchStyle(selectedPreset.preview);
  }, [backgroundId, selectedPreset]);

  function saveDraft() {
    const draft: CreatePostDraft = {
      content: noiDung,
      selectedGroupIds: [...daChon],
      anonymous: anDanh,
      highlight: neuBat,
      draftCrossPostEnabled: dangCheo,
      postToProfile: dangLenTrang,
      delayFromSec: tuGiay,
      delayToSec: denGiay,
      antiSpam: neSpam,
      enableFirstComment: batBinhLuan,
      firstComment: binhLuan,
      scheduledAt: henGio,
      repeatDaily: lapHangNgay,
      media: media.map(item => item.record),
      backgroundId,
      updatedAt: Date.now(),
    };
    try {
      localStorage.setItem(buildDraftStorageKey(tenantId, fbUid), JSON.stringify(draft));
      setThongBao({ loai: 'xong', chu: 'Đã lưu bản nháp trên trình duyệt này.' });
    } catch {
      setThongBao({ loai: 'loi', chu: 'Không lưu được bản nháp. Bộ nhớ trình duyệt có thể đã đầy.' });
    }
  }

  function validateMediaFiles(files: File[]): string | null {
    if (!tenantId) return 'Không xác định được workspace để lưu media.';
    if (files.length === 0) return null;
    if (files.some(file => !MEDIA_MIME.has(file.type as MediaMimeType))) return 'Chỉ hỗ trợ JPEG, PNG, WebP hoặc MP4.';
    const records = media.map(item => item.record);
    const hasVideo = records.some(item => item.mimeType === 'video/mp4');
    const newVideos = files.filter(file => file.type === 'video/mp4');
    const newPhotos = files.filter(file => file.type !== 'video/mp4');
    if ((hasVideo || newVideos.length > 0) && !mediaStatus.videoEnabled) {
      return formatPlannedActionNotice(capabilities, CAPABILITY_KEYS.POST_GROUP_VIDEO, 'Tính năng đăng video tạm thời chưa mở.');
    }
    if (newPhotos.length > 0 && !mediaStatus.photoEnabled) {
      return formatPlannedActionNotice(capabilities, CAPABILITY_KEYS.POST_GROUP_PHOTO, 'Tính năng đăng ảnh tạm thời chưa mở.');
    }
    if (hasVideo || newVideos.length > 0) {
      if (records.length > 0 || files.length !== 1 || newVideos.length !== 1) return 'Video phải được đăng riêng, không trộn với ảnh hoặc video khác.';
      if (newVideos[0].size > MAX_VIDEO_BYTES) return 'Video tối đa 500MB.';
      return null;
    }
    if (records.length + files.length > MAX_IMAGES) return 'Mỗi bài hỗ trợ tối đa 10 ảnh.';
    if (files.some(file => file.size <= 0 || file.size > MAX_IMAGE_BYTES)) return 'Mỗi ảnh phải nhỏ hơn 10MB.';
    const total = records.reduce((sum, item) => sum + item.size, 0) + files.reduce((sum, file) => sum + file.size, 0);
    return total > MAX_TOTAL_IMAGE_BYTES ? 'Tổng dung lượng ảnh mỗi bài tối đa 50MB.' : null;
  }

  async function handleSelectMedia(event: ChangeEvent<HTMLInputElement>) {
    const files = [...(event.target.files || [])];
    event.target.value = '';
    const error = validateMediaFiles(files);
    if (error) { setThongBao({ loai: 'loi', chu: error }); return; }
    if (!files.length || !tenantId) return;
    setIsImportingMedia(true);
    setMediaProgress(0);
    const imported: MediaUiItem[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const record = await mediaVault.importFile(tenantId, file, (loaded, total) => {
          const local = total > 0 ? loaded / total : 0;
          setMediaProgress(Math.round(((i + local) / files.length) * 100));
        });
        imported.push({ record, previewUrl: URL.createObjectURL(file) });
      }
      setMedia(current => [...current, ...imported]);
      setMediaProgress(100);
      if (backgroundId) {
        setBackgroundId(null);
        setThongBao({ loai: 'loi', chu: 'Đã tắt phông nền: Không thể dùng phông nền cùng ảnh hoặc video.' });
      } else {
        setThongBao({ loai: 'xong', chu: `Đã lưu ${imported.length} tệp vào Extension trên thiết bị này.` });
      }
    } catch (error) {
      for (const item of imported) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        try { await mediaVault.remove(tenantId, item.record.localMediaId); } catch {}
      }
      setThongBao({ loai: 'loi', chu: error instanceof Error ? error.message : 'Không lưu được media vào Extension.' });
    } finally { setIsImportingMedia(false); }
  }

  async function handleRemoveMedia(item: MediaUiItem) {
    try {
      if (tenantId) await mediaVault.remove(tenantId, item.record.localMediaId);
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      setMedia(current => current.filter(x => x.record.localMediaId !== item.record.localMediaId));
    } catch (error) {
      setThongBao({ loai: 'loi', chu: error instanceof Error ? error.message : 'Không xóa được media.' });
    }
  }

  async function gui() {
    if (isUnpaid) {
      onYeuCauNangCap?.();
      setThongBao({ loai: 'loi', chu: 'Cần kích hoạt hoặc gia hạn gói dịch vụ để thực hiện gửi bài.' });
      return;
    }
    if (loiHopLe) { setThongBao({ loai: 'loi', chu: loiHopLe }); return; }
    setDangGui(true);
    setThongBao(null);
    try {
      // Đăng chéo là MỘT bài đăng vào nhóm gốc rồi Facebook tự nhân bản sang các
      // nhóm còn lại, nên `targets` chỉ được chứa nhóm gốc. Gửi cả danh sách vào
      // `targets` khiến máy chủ lọc sạch `crossPostGroupIds` và đăng chéo mất hiệu
      // lực âm thầm — xem chiaNhomDangCheo() và tests/dangCheoHieuLuc.test.js.
      const chiaNhom = dangCheo ? chiaNhomDangCheo([...daChon]) : null;

      const than: Record<string, unknown> = {
        message: noiDung,
        targets: chiaNhom ? chiaNhom.targets : [...daChon],
        delayFromSec: tuGiay,
        delayToSec: denGiay,
        safeMode: neSpam,
        fbUid,
      };
      if (dangLenTrang) than.postToFeed = true;
      if (choPhepAnDanh(dangDungPage, anDanh)) than.isAnonymous = true;
      if (neuBat) than.useHighlight = true;
      if (chiaNhom) { than.crossPost = true; than.crossPostGroupIds = chiaNhom.crossPostGroupIds; }
      if (batBinhLuan && binhLuan.trim()) than.firstComment = binhLuan.trim();
      if (henGio) than.scheduledAt = new Date(henGio).getTime();
      if (lapHangNgay) than.repeatDaily = true;
      if (media.length) than.media = media.map(item => item.record);
      if (backgroundId) {
        const applied = applyBackgroundToPayload(than, backgroundId, {
          backgroundId,
          presets: presetsCatalog,
          groupIdCount: daChon.size,
          isPage: dangDungPage,
          postToProfile: dangLenTrang,
          anDanh,
          isCrossPost: dangCheo,
          hasMedia: media.length > 0,
          text: noiDung,
          charCount: noiDung.length,
          lineCount: noiDung.split('\n').length,
        });
        if (!applied.ok) {
          setThongBao({ loai: 'loi', chu: applied.error });
          setDangGui(false);
          return;
        }
      }

      const kq = await api.taoChienDich(than);
      localStorage.removeItem(buildDraftStorageKey(tenantId, fbUid));
      setThongBao({ loai: 'xong', chu: kq.message || 'Đã tạo tác vụ đăng bài.' });
      setNoiDung('');
      contentRef.current = '';
      setDaChon(new Set());
      setBinhLuan('');
      setBackgroundId(null);
      setShowColorPalette(false);
      for (const item of media) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      setMedia([]);
      onXong?.();
    } catch (e) {
      const resolution = resolveSubmitAccessError(e, onYeuCauNangCap);
      if (resolution.kind === 'PAYMENT_REQUIRED' || resolution.kind === 'SUBSCRIPTION_EXPIRED') {
        setIsUnpaid(true);
      }
      setThongBao({ loai: 'loi', chu: resolution.userMessage });
    } finally {
      setDangGui(false);
    }
  }

  /**
   * ===== AI SPIN NỘI DUNG =====
   *
   * Người dùng bôi đen một đoạn rồi bấm nút. Ô nhập biến đoạn đó thành `{{đoạn}}`,
   * máy chủ viết lại thành cú pháp spin `{a|b|c}` và trả về nguyên bài.
   *
   * VÌ SAO BỌC `{{...}}` THAY VÌ GỬI RIÊNG ĐOẠN BÔI ĐEN
   *
   * Máy chủ cần thấy TOÀN BỘ bài để trả về nguyên văn phần không được chọn
   * (aiRewrite.js:139-146 tìm marker trong cả chuỗi). Gửi riêng đoạn thì mất ngữ
   * cảnh, và quan trọng hơn: số điện thoại, giá tiền, đường link nằm ngoài vùng
   * bôi đen phải giữ NGUYÊN TỪNG KÝ TỰ — đó là tiền thật của khách.
   *
   * VÌ SAO SỐ BIẾN THỂ = SỐ MỤC TIÊU
   *
   * Lúc đăng, `spinMany(message, targets.length)` chia mỗi nhóm một biến thể
   * (dispatch.routes.js:91). Xin đúng bằng số mục tiêu thì lý tưởng nhất là mỗi
   * nhóm một nội dung khác nhau — đăng y hệt vào hàng chục nhóm là dấu hiệu spam
   * rõ nhất Facebook dùng để phát hiện tool.
   *
   * Nhưng máy chủ ÉP về khoảng 2..6 (aiRewrite.js:148). Chọn 20 nhóm thì vẫn chỉ
   * có 6 biến thể. Ta KHÔNG giả vờ điều đó không xảy ra: `spun.exhausted` lúc đăng
   * sẽ báo thẳng số nhóm bị trùng (dispatch.routes.js:212-216).
   *
   * ===== HAI CÁI BẪY ĐÃ TRÁNH =====
   *
   * 1. `noiDung` trong state có thể LỆCH với DOM nếu người dùng gõ tiếp trong lúc
   *    chờ mạng. Nên cắt chuỗi theo `noiDung` (nguồn sự thật của React), và sau khi
   *    có kết quả thì kiểm tra lại bài có đổi không trước khi ghi đè.
   *
   * 2. Không tự bọc `{{}}` khi người dùng chưa bôi đen gì. Bọc cả bài rồi nhờ AI
   *    viết lại toàn bộ sẽ nuốt mất số điện thoại và giá. Thà báo "hãy bôi đen".
   */
  async function aiSpin() {
    const o = oNhap.current;
    if (!o) return;

    const dau = o.selectionStart ?? 0;
    const cuoi = o.selectionEnd ?? 0;
    const currentContent = contentRef.current;
    const doanChon = currentContent.slice(dau, cuoi).trim();

    if (!doanChon) {
      setThongBao({
        loai: 'loi',
        chu: 'Hãy bôi đen đoạn muốn AI viết lại, rồi bấm lại nút này. AI chỉ đổi phần được chọn — số điện thoại, giá và link ngoài vùng chọn được giữ nguyên.',
      });
      return;
    }

    // Đã có marker rồi thì bọc thêm lần nữa sẽ thành {{{{...}}}} và regex của máy
    // chủ bắt nhầm biên. Chặn tại đây, nói rõ vì sao.
    if (currentContent.slice(dau, cuoi).includes('{{') || currentContent.slice(dau, cuoi).includes('}}')) {
      setThongBao({ loai: 'loi', chu: 'Đoạn đang chọn đã được đánh dấu rồi. Hãy chọn một đoạn khác.' });
      return;
    }

    const baiLucGui = currentContent;
    const danhDau = currentContent.slice(0, dau) + '{{' + doanChon + '}}' + currentContent.slice(cuoi);

    // Hiện marker ngay để người dùng thấy đúng đoạn nào sắp bị đổi.
    setNoiDung(danhDau);
    contentRef.current = danhDau;
    setThongBao(null);

    try {
      // Xin đúng số mục tiêu; máy chủ tự ép về trần của nó.
      const kq = await api.aiVietLai(danhDau, Math.max(2, soMucTieu));

      // Người dùng có thể đã gõ tiếp trong lúc chờ. Ghi đè lúc đó là xoá mất chữ
      // họ vừa viết — kiểu mất dữ liệu người dùng không bao giờ tha thứ.
      if (contentRef.current !== danhDau) {
        setThongBao({ loai: 'loi', chu: 'Bài đã thay đổi trong lúc chờ AI nên kết quả bị bỏ qua. Hãy bôi đen lại và thử lần nữa.' });
        return;
      }

      setNoiDung(kq.text);
      contentRef.current = kq.text;

      // Nói thật số biến thể nhận được, KHÔNG nói số đã xin. Có cảnh báo thì
      // chuyển tiếp nguyên văn thay vì báo thành công trơn.
      const canhBao = (kq.segments || []).find(s => s.canhBao);
      if (canhBao) {
        setThongBao({ loai: 'loi', chu: `AI không tạo được biến thể mới cho đoạn này. Bài giữ nguyên — hãy thử chọn đoạn dài hơn.` });
        return;
      }
      const soBienThe = kq.segments?.[0]?.bienThe.length ?? 0;
      const thieu = soBienThe > 0 && soBienThe < soMucTieu;
      setThongBao({
        loai: 'xong',
        chu: (kq.message || 'Đã tạo biến thể.') +
          (thieu
            ? ` Được ${soBienThe} biến thể cho ${soMucTieu} mục tiêu nên vài nhóm sẽ nhận nội dung trùng. Bôi đen thêm đoạn khác để đa dạng hơn.`
            : ''),
      });
    } catch (e) {
      // Trả bài về đúng trạng thái trước khi bấm: để lại `{{...}}` lơ lửng thì
      // người dùng tưởng đã spin xong.
      if (contentRef.current === danhDau) {
        setNoiDung(baiLucGui);
        contentRef.current = baiLucGui;
      }
      setThongBao({ loai: 'loi', chu: e instanceof LoiMayChu ? e.message : 'Không gọi được AI. Hãy thử lại.' });
    }
  }

  function nutCongCu(
    Icon: typeof VenetianMask, nhan: string, bat: boolean,
    doi: () => void, khoa = false, lyDoKhoa?: string,
  ) {
    const isPalette = Icon === Palette;
    return (
      <button
        type="button"
        title={nhan}
        aria-label={nhan}
        aria-disabled={khoa || undefined}
        onClick={khoa ? () => { if (lyDoKhoa) setThongBao({ loai: 'loi', chu: lyDoKhoa }); } : doi}
        className={NUT_CONG_CU + ' ' + (khoa ? NUT_KHOA : bat ? NUT_BAT : NUT_THUONG)}
      >
        {isPalette ? (
          <span className="flex items-center gap-0.5 font-semibold text-xs leading-none">
            <span className="text-[11px] font-extrabold tracking-tight">Aa</span>
            <Palette className="h-3 w-3 opacity-80" />
          </span>
        ) : (
          <Icon className="h-4 w-4" />
        )}
      </button>
    );
  }

  const handleToggleColorPalette = () => {
    setShowColorPalette((v) => {
      const next = !v;
      if (next && presetsCatalog === null && !isLoadingPresets) {
        fetchPresets();
      }
      return next;
    });
  };

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in">
      {/*
        ===== KHOÁ CỨNG CHIỀU CAO — CUỘN ĐỘC LẬP (22/09/2026) =====

        Trước đây dùng `min-h-[...]`. CSS Grid kéo giãn hai cột bằng nhau, nên
        danh sách nhóm dài bao nhiêu thì CẢ HAI cột cao bấy nhiêu — kể cả cột
        soạn bài vốn không có gì để kéo dài.

        Đo thật trên production (CDP, tiêm 100 nhóm vào DOM):
            khung nhìn      897px
            trang phình     8337px   (9,3 lần)
            ô nhập          7710px   <- `flex-1` hút sạch chỗ thừa
            nút "Đăng vào"  top 8244 <- NGOÀI khung nhìn
            danh sách nhóm  tuCuon = false  <- không cuộn, nó phình

        Khách có 100 nhóm phải cuộn 8000px mới bấm được nút Gửi.

        `h-` thay `min-h-` khoá chiều cao tổng lại; phần tràn được xử lý bên
        trong từng cột chứ không đẩy cả trang dài ra.

        `min-h-0` trên cả hai cột là BẮT BUỘC: mục flex/grid mặc định có
        `min-height: auto`, nghĩa là không co nhỏ hơn nội dung — `overflow`
        con sẽ không bao giờ kích hoạt. Thiếu nó thì hai dòng overflow dưới
        đây vô tác dụng.
      */}
      <div className="grid h-[calc(100vh-8.5rem)] gap-5 xl:grid-cols-[minmax(0,1fr)_440px]">

        {/* ===== Cột trái: soạn bài (cuộn riêng) ===== */}
        <The className="flex min-h-0 flex-col overflow-y-auto p-5 scrollbar-thin">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Soạn bài đăng</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Ảnh/video được lưu trên thiết bị trong Extension và upload trực tiếp tới Facebook.
            </p>
          </div>

          {/* Static Preview Explanatory Notice for Unpaid Workspace (UI-04) */}
          {isUnpaid && (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50 p-3.5 text-xs text-brand-900 dark:border-brand-900/40 dark:bg-brand-950/20 dark:text-brand-300">
              <div>
                <span className="font-bold">Bản xem trước tĩnh (Freemium Preview): </span>
                <span>Gói dịch vụ cần được kích hoạt hoặc gia hạn để đăng bài thật lên Facebook.</span>
              </div>
              {onYeuCauNangCap && (
                <button
                  type="button"
                  onClick={onYeuCauNangCap}
                  className="shrink-0 rounded-lg bg-brand-600 px-3 py-1.5 font-semibold text-white shadow-sm hover:bg-brand-700 transition"
                >
                  Nâng cấp gói
                </button>
              )}
            </div>
          )}

          {/* Page posting capability notice: Page can view groups but posting is disabled in MVP */}
          {dangDungPage && !isPagePostingEnabled && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-300">
              <span className="font-semibold">Chưa mở: </span>
              {getCapabilityReason(
                capabilities,
                CAPABILITY_KEYS.POST_PAGE_TEXT,
                'Tính năng đăng bài với tư cách Fanpage tạm khóa trong phiên bản MVP; tài khoản Trang chỉ hỗ trợ xem danh sách.'
              )}
            </div>
          )}

          {/*
            ===== CHẾ ĐỘ ĐĂNG BÀI — TÁCH LÊN TRÊN Ô NHẬP (22/09/2026) =====

            Ba nút này quyết định bài đi ĐÂU: ẩn danh, đăng chéo, đăng lên trang
            cá nhân. Chúng phải được quyết định TRƯỚC khi viết, không phải sau —
            nên vị trí đúng là trên ô nhập, ngay dưới tiêu đề.

            Thanh đáy giờ chỉ còn nút ảnh hưởng tới NỘI DUNG đang gõ: nêu bật,
            phông nền, trình soạn thảo, emoji.

            Nhóm này KHÔNG mang vạch ngăn như thanh đáy — nó đã là một khối
            riêng, thêm vạch là chia nhỏ thứ vốn đã tách bạch.
          */}
          <div className="mt-4 flex flex-wrap items-center gap-1">
            {nutCongCu(VenetianMask, !isAnonEnabled ? formatPlannedActionNotice(capabilities, CAPABILITY_KEYS.POST_GROUP_ANON_TEXT, 'Chưa hoàn tất nghiệm thu chế độ ẩn danh trong bản phát hành này.') : (dangDungPage ? 'Page không đăng ẩn danh — chuyển sang tài khoản cá nhân' : 'Đăng ẩn danh — chỉ dùng cho Nhóm'), anDanh, () => setAnDanh(v => !v), dangDungPage || !isAnonEnabled, !isAnonEnabled ? formatPlannedActionNotice(capabilities, CAPABILITY_KEYS.POST_GROUP_ANON_TEXT, 'Chưa hoàn tất nghiệm thu chế độ ẩn danh trong bản phát hành này.') : (dangDungPage ? 'Page không đăng ẩn danh — chuyển sang tài khoản cá nhân' : undefined))}
            {nutCongCu(Share2, !isCrossPostEnabled ? formatPlannedActionNotice(capabilities, CAPABILITY_KEYS.POST_GROUP_CROSSPOST, 'Tính năng đăng chéo tạm khóa trong phiên bản MVP chờ kiểm chứng đối soát phân phối nhóm phụ.') : (hasAttachedMedia ? 'Đăng chéo chưa hỗ trợ bài có ảnh/video' : 'Đăng chéo — một bài hiển thị ở nhiều nhóm cùng lúc (tối đa 9 nhóm)'), dangCheo, () => setDangCheo(v => !v), !isCrossPostEnabled || hasAttachedMedia, !isCrossPostEnabled ? formatPlannedActionNotice(capabilities, CAPABILITY_KEYS.POST_GROUP_CROSSPOST, 'Tính năng đăng chéo tạm khóa trong phiên bản MVP chờ kiểm chứng đối soát phân phối nhóm phụ.') : (hasAttachedMedia ? 'Đăng chéo chưa hỗ trợ bài có ảnh/video' : undefined))}
            {nutCongCu(Users, !isProfileEnabled ? formatPlannedActionNotice(capabilities, CAPABILITY_KEYS.POST_PROFILE_TEXT, 'Tính năng đăng lên dòng thời gian cá nhân (Profile Feed) tạm khóa trong phiên bản MVP chờ kiểm chứng an toàn.') : 'Đăng lên Trang cá nhân', dangLenTrang, () => setDangLenTrang(v => !v), !isProfileEnabled, !isProfileEnabled ? formatPlannedActionNotice(capabilities, CAPABILITY_KEYS.POST_PROFILE_TEXT, 'Tính năng đăng lên dòng thời gian cá nhân (Profile Feed) tạm khóa trong phiên bản MVP chờ kiểm chứng an toàn.') : undefined)}
          </div>

          {/* Visible informational notices for preserved blocked intents from drafts without silent rerouting (UI-04 / DATA-02) */}
          {dangLenTrang && !isProfileEnabled && (
            <div className="mt-2.5 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-300">
              <div>
                <span className="font-bold">Ý định đăng lên Trang cá nhân đang tạm khóa trong MVP: </span>
                <span>{getCapabilityReason(capabilities, CAPABILITY_KEYS.POST_PROFILE_TEXT, 'Tính năng đăng lên dòng thời gian cá nhân (Profile Feed) tạm khóa trong phiên bản MVP chờ kiểm chứng an toàn.')} Nút gửi được giữ khóa để tránh gửi nhầm nhóm.</span>
              </div>
              <button
                type="button"
                onClick={() => setDangLenTrang(false)}
                className="shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 transition"
              >
                Tắt để đăng vào nhóm
              </button>
            </div>
          )}
          {anDanh && !isAnonEnabled && (
            <div className="mt-2.5 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-300">
              <div>
                <span className="font-bold">Ý định đăng ẩn danh đang tạm khóa: </span>
                <span>{getCapabilityReason(capabilities, CAPABILITY_KEYS.POST_GROUP_ANON_TEXT, 'Chưa hoàn tất nghiệm thu chế độ ẩn danh trong bản phát hành này.')} Nút gửi được giữ khóa để không lộ danh tính thật.</span>
              </div>
              <button
                type="button"
                onClick={() => setAnDanh(false)}
                className="shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 transition"
              >
                Tắt đăng ẩn danh
              </button>
            </div>
          )}
          {dangCheo && !isCrossPostEnabled && (
            <div className="mt-2.5 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-300">
              <div>
                <span className="font-bold">Ý định đăng chéo đang tạm khóa: </span>
                <span>{getCapabilityReason(capabilities, CAPABILITY_KEYS.POST_GROUP_CROSSPOST, 'Tính năng đăng chéo tạm khóa trong phiên bản MVP chờ kiểm chứng đối soát phân phối nhóm phụ.')}</span>
              </div>
              <button
                type="button"
                onClick={() => setDangCheo(false)}
                className="shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 transition"
              >
                Tắt đăng chéo
              </button>
            </div>
          )}

          <div
            style={composerCardStyle}
            className={`relative overflow-hidden mt-3 flex min-h-[240px] flex-1 flex-col rounded-xl border transition-colors ${
              backgroundId
                ? (composerCardStyle
                    ? 'border-brand-400 text-white shadow-inner'
                    : 'border-slate-400 bg-slate-800 text-white shadow-inner')
                : 'border-slate-200 bg-slate-50/50 dark:border-slate-700 dark:bg-slate-900/40'
            }`}
          >
            {/* Authenticated image preview asset without obscuring textarea */}
            {selectedPreset?.preview?.type === 'image' && selectedPreset.preview.assetUrl && (
              <img
                src={selectedPreset.preview.assetUrl}
                alt=""
                aria-hidden="true"
                loading="eager"
                crossOrigin="use-credentials"
                className="absolute inset-0 h-full w-full rounded-xl object-cover pointer-events-none"
                onError={(e) => {
                  (e.currentTarget as HTMLElement).style.display = 'none';
                }}
              />
            )}
            <textarea
              ref={oNhap}
              value={noiDung}
              onChange={(e) => {
                contentRef.current = e.target.value;
                setNoiDung(e.target.value);
                if (thongBao) setThongBao(null);
              }}
              placeholder="Bạn muốn chia sẻ nội dung gì?"
              className={`relative z-10 flex-1 resize-none bg-transparent px-4 py-3 text-sm leading-6 outline-none ${
                backgroundId
                  ? 'text-white placeholder-slate-200 text-center font-bold'
                  : 'text-slate-700 dark:text-slate-200'
              }`}
            />
            {/*
              ===== THANH ĐÁY — CHỈ CÒN NÚT TÁC ĐỘNG TỚI NỘI DUNG =====

              Ba nút chế độ đăng bài (ẩn danh, đăng chéo, trang cá nhân) đã tách
              lên trên ô nhập ngày 22/09/2026. Chúng quyết định bài đi ĐÂU, nên
              phải chọn trước khi viết.

              Còn lại ở đây là nút ảnh hưởng tới CHÍNH NỘI DUNG đang gõ:
                  nêu bật · phông nền · trình soạn thảo · emoji

              "Nêu bật" mang icon `@` theo yêu cầu của chủ dự án. Icon này từng
              thuộc nút "Gắn thẻ bạn bè" — nút đó đã bị xoá vì luôn khoá và
              không có kế hoạch làm.

              Nút "Phông nền" luôn mở được bảng chọn kể cả khi bài dài (không khoá nút chết);
              giới hạn 130 ký tự / 4 dòng đã được gỡ bỏ per user request.
            */}
            <div className="relative z-10 flex items-center justify-between px-2 pb-2">
              <div className="relative flex items-center gap-0.5">
                {nutCongCu(AtSign, 'Nêu bật — tự tăng giãn cách 3 lần để tránh bị khoá tài khoản', neuBat, () => setNeuBat(v => !v))}
                <div ref={colorPaletteRef} className="relative">
                  {nutCongCu(Palette, backgroundIncompatibilityReason || (backgroundId ? `Phông nền: ${selectedPreset ? `${selectedPreset.label} (${selectedPreset.id})` : backgroundId} (bấm để đổi)` : 'Phông nền — Bấm để chọn phông nền cho bài viết nhóm'), Boolean(backgroundId) || showColorPalette, handleToggleColorPalette, false)}
                  <BackgroundPickerDialog
                    isOpen={showColorPalette}
                    onClose={() => setShowColorPalette(false)}
                    backgroundId={backgroundId}
                    onSelectBackground={(id) => setBackgroundId(id)}
                    presets={presetsCatalog}
                    isLoading={isLoadingPresets}
                    error={presetsError}
                    onRefresh={() => fetchPresets(true)}
                    tenantId={tenantId}
                    actorId={fbUid}
                  />
                  {/* Hỗ trợ tải lại danh mục: title="Tải lại danh mục phông nền từ máy chủ" */}
                </div>
                {nutCongCu(Type, 'Trình soạn thảo văn bản - Sắp có', false, () => {}, true)}
                {nutCongCu(Smile, 'Chèn emoji - Sắp có', false, () => {}, true)}
              </div>

              <button
                type="button"
                onClick={() => chaySpin(aiSpin)}
                disabled={!ai?.available || dangSpin}
                aria-busy={dangSpin || undefined}
                title={ai?.available ? 'Bôi đen đoạn muốn viết lại rồi bấm' : (ai?.reason || 'AI chưa sẵn sàng')}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-semibold text-violet-700 transition disabled:cursor-not-allowed disabled:opacity-50 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-400"
              >
                {dangSpin
                  ? <span aria-hidden="true" className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" />
                  : <Sparkles className="h-3.5 w-3.5" />}
                {dangSpin ? 'Đang viết lại...' : 'AI Spin nội dung'}
              </button>
            </div>
          </div>

          <div className="mt-2 flex justify-between items-center text-xs text-slate-400">
            <div className="flex items-center gap-2">
              {anDanh && <span className="text-amber-600">Đăng ẩn danh — chỉ dùng cho Nhóm</span>}
              {backgroundId && (
                <span className="inline-flex items-center gap-1 rounded bg-brand-100 px-2 py-0.5 font-semibold text-brand-800 dark:bg-brand-900/40 dark:text-brand-300">
                  Phông nền: {selectedPreset ? `${selectedPreset.label} (${selectedPreset.id})` : backgroundId}
                </span>
              )}
            </div>
            <span className={noiDung.length > TOI_DA_KY_TU ? 'text-red-600 font-bold' : undefined}>
              {noiDung.length.toLocaleString('vi-VN')} ký tự
            </span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <input
              ref={mediaInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,video/mp4"
              className="hidden"
              onChange={handleSelectMedia}
            />
            {vaultReady ? (
              <button
                type="button"
                disabled={isImportingMedia || !mediaStatus.canAttachMedia}
                onClick={() => mediaInputRef.current?.click()}
                title={
                  !mediaStatus.canAttachMedia
                    ? (mediaStatus.unsupportedMediaReason || 'Chưa mở tính năng đính kèm media')
                    : 'Ảnh/video được lưu cục bộ trong Extension trên thiết bị này'
                }
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
              >
                <Upload className="h-4 w-4" />
                {!mediaStatus.canAttachMedia
                  ? 'Chưa mở đính kèm media'
                  : isImportingMedia
                  ? `Đang lưu media ${mediaProgress}%`
                  : 'Đính kèm ảnh/video'}
              </button>
            ) : (
              <button
                type="button"
                disabled
                title="Đang hoàn thiện, chưa thể đính kèm media"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
              >
                <Upload className="h-4 w-4" />
                Đang hoàn thiện, chưa thể đính kèm media
              </button>
            )}
            <button type="button" onClick={saveDraft} className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white">
              Lưu bản nháp
            </button>
          </div>

          {media.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {media.map(item => (
                <div key={item.record.localMediaId} className="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
                  {item.record.mimeType.startsWith('image/') && item.previewUrl ? (
                    <img src={item.previewUrl} alt={item.record.name} className="h-28 w-full object-cover" />
                  ) : (
                    <div className="flex h-28 items-center justify-center bg-slate-900 px-3 text-center text-xs font-semibold text-white">
                      Video MP4<br />{item.record.name}
                    </div>
                  )}
                  <div className="truncate px-2 py-1.5 text-xs text-slate-600 dark:text-slate-300">
                    {item.record.name} · {(item.record.size / 1024 / 1024).toFixed(1)}MB
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveMedia(item)}
                    aria-label={`Xóa ${item.record.name}`}
                    className="absolute right-1.5 top-1.5 rounded-full bg-slate-950/75 px-2 py-1 text-xs font-bold text-white"
                  >×</button>
                </div>
              ))}
            </div>
          )}

          {/* Giãn cách + né spam */}
          <div className="mt-5 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Cách nhau</span>
                <input
                  type="number" value={tuGiay} min={neuBat ? GIAN_CACH_TOI_THIEU_NEU_BAT : GIAN_CACH_TOI_THIEU}
                  onChange={(e) => setTuGiay(Number(e.target.value))} className={O_SO}
                />
                <span className="text-xs text-slate-400">đến</span>
                <input
                  type="number" value={denGiay} max={GIAN_CACH_TOI_DA}
                  onChange={(e) => setDenGiay(Number(e.target.value))} className={O_SO}
                />
                <span className="text-xs text-slate-500 dark:text-slate-400">giây mỗi nhóm</span>
              </div>
              <button type="button" onClick={() => setNeSpam(v => !v)} className="flex items-center gap-2">
                <CongTac bat={neSpam} />
                <ShieldCheck className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Né spam</span>
              </button>
            </div>
            {neuBat && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                Nêu bật đang bật: giãn cách tối thiểu {GIAN_CACH_TOI_THIEU_NEU_BAT} giây để tránh Facebook khoá tài khoản.
              </p>
            )}
          </div>

          {/* Bình luận sau khi đăng */}
          <div className={KHOI_TUY_CHON}>
            <button type="button" onClick={() => setBatBinhLuan(v => !v)} className="flex items-center gap-2">
              <CongTac bat={batBinhLuan} />
              <MessageSquare className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Bình luận sau khi đăng</span>
            </button>
            {batBinhLuan && (
              <textarea
                value={binhLuan}
                onChange={(e) => setBinhLuan(e.target.value)}
                placeholder="Nội dung bình luận tự đăng sau khi bài lên..."
                rows={2}
                className="mt-2 w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            )}
          </div>

          {/* Thời gian đăng */}
          <div className={KHOI_TUY_CHON}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Thời gian đăng</span>
              </div>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox" checked={lapHangNgay} onChange={(e) => setLapHangNgay(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                Lặp lại hàng ngày
              </label>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                type="datetime-local" value={henGio} onChange={(e) => setHenGio(e.target.value)}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 outline-none focus:border-brand-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
              />
              <span className="text-xs text-slate-400">Bỏ trống để đăng ngay</span>
            </div>
          </div>
        </The>

        {/*
          ===== Cột phải: chọn nhóm =====

          `overflow-hidden` cắt phần tràn ở tầng thẻ, để CHỈ khu danh sách ở
          giữa được cuộn. Nhờ vậy ô tìm kiếm luôn dính trên, nút "Đăng vào N
          mục tiêu" luôn dính dưới — bấm được bất kể khách có bao nhiêu nhóm.
        */}
        <The className="flex min-h-0 flex-col overflow-hidden">
          <div className="border-b p-4 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-slate-900 dark:text-white">Chọn nhóm đăng</h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Đã chọn {daChon.size}/{nhom.length} nhóm
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDaChon(daChon.size === nhomLoc.length ? new Set() : new Set(nhomLoc.map(g => g.id)))}
                className="px-3 py-2 text-xs font-semibold text-brand-600 dark:text-brand-400"
              >
                {daChon.size === nhomLoc.length && nhomLoc.length > 0 ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
              </button>
            </div>
            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={timNhom} onChange={(e) => setTimNhom(e.target.value)} placeholder="Tìm nhóm..."
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>

          {/*
            `min-h-0` thay cho `min-h-[360px]`: sàn 360px chặn khu này co lại,
            nên trên cửa sổ thấp nó lại đẩy header/footer tràn khỏi thẻ — đúng
            thứ `overflow-hidden` ở trên vừa chặn. Đây là khu DUY NHẤT được
            cuộn trong cột phải.
          */}
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3 scrollbar-thin">
            {dangTaiNhom ? (
              <DangTai dong={5} />
            ) : nhomLoc.length === 0 ? (
              <ChuaCoDuLieu
                chu={timNhom ? 'Không tìm thấy nhóm nào khớp.' : 'Chưa có nhóm nào.'}
                goiY={lyDoTrongNhom || (timNhom ? undefined : 'Vào Kênh & nhóm để đồng bộ danh sách.')}
              />
            ) : (
              nhomLoc.map((g) => {
                const chon = daChon.has(g.id);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => {
                      const m = new Set(daChon);
                      if (m.has(g.id)) m.delete(g.id); else m.add(g.id);
                      setDaChon(m);
                    }}
                    className={
                      'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ' +
                      (chon
                        ? 'border-brand-500 bg-brand-50/50 dark:border-brand-500 dark:bg-brand-500/10'
                        : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/40')
                    }
                  >
                    <span className={
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border ' +
                      (chon ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 dark:border-slate-600')
                    }>
                      {chon && <span className="text-[11px] leading-none">✓</span>}
                    </span>
                    <AnhDaiDien src={g.avatar} ten={g.name} kichThuoc="h-8 w-8" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{g.name}</span>
                      <span className="text-xs text-slate-400">Nhóm Facebook</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t p-4 dark:border-slate-700">
            {thongBao && (
              <div className={
                'mb-3 flex items-start justify-between gap-2 rounded-lg px-3 py-2 text-xs ' +
                (thongBao.loai === 'loi'
                  ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'
                  : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400')
              }>
                <span className="flex-1">{thongBao.chu}</span>
                <button
                  type="button"
                  onClick={() => setThongBao(null)}
                  className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  title="Đóng thông báo"
                  aria-label="Đóng thông báo"
                >
                  ✕
                </button>
              </div>
            )}
            {loiHopLe && !thongBao && (
              <p className="mb-3 text-xs text-slate-400">{loiHopLe}</p>
            )}
            <button
              type="button"
              onClick={gui}
              disabled={dangGui || Boolean(loiHopLe)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {dangGui ? 'Đang gửi...' : `Đăng vào ${soMucTieu} mục tiêu`}
            </button>
          </div>
        </The>
      </div>
    </div>
  );
}
